import {
  Box,
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { HookFormText, HookFormTextarea } from 'components/hookForm';

import styles from './messengerSecretModal.module.scss';

export type NewSecret = {
  secret: string;
  description?: string;
};

export type AddSecretModalComponentProps = {
  /** Whether the modal is open. */
  isOpen: boolean;
  /** Whether the form is submitting. */
  isSubmitting: boolean;
  /** Automatically generated secret. */
  autoGeneratedSecret?: string;
  /**
   * Callback for adding new secret.
   *
   * @param secret - New secret
   */
  onAdd: (secret: NewSecret) => void;
  /** Callback for hiding the modal. */
  hide: () => void;
};

/** Presentational component for adding messenger secret. */
export const AddSecretModalComponent = ({
  isOpen,
  isSubmitting,
  autoGeneratedSecret,
  onAdd,
  hide,
}: AddSecretModalComponentProps): JSX.Element => {
  const { control, handleSubmit, reset } = useForm<NewSecret>({
    mode: 'all',
    defaultValues: {
      secret: autoGeneratedSecret,
      description: '',
    },
  });

  useEffect(() => {
    reset({ secret: autoGeneratedSecret });
  }, [autoGeneratedSecret, reset]);

  return (
    <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
      <ModalHead
        className={styles.modalHeader}
        title="Add new key"
        onClose={hide}
      />

      <ModalBody className={styles.modalBody}>
        <FlexBox flexDirection="column" gap="300">
          <Text.Paragraph>
            You can save the automatically generated key below, or edit it and
            set your own. The key is expected to be base 64 url encoded.&nbsp;
            <strong>
              Note that you won't be able to view or edit this key after you
              save it.
            </strong>
            &nbsp;In case of changes, you must create a new key.
          </Text.Paragraph>

          {autoGeneratedSecret ? (
            <FormGrid>
              <FormField>
                <HookFormTextarea name="secret" label="Key" control={control} />
              </FormField>
              <FormField>
                <HookFormText
                  name="description"
                  label="Key description"
                  control={control}
                  helpIcon={{
                    tooltipClassName: styles.helpTooltip,
                    text: (
                      <Text.Paragraph variant="2" color="white">
                        A description will help you identify your key.
                        <br />
                        You can edit it later if necessary.
                      </Text.Paragraph>
                    ),
                  }}
                />
              </FormField>
            </FormGrid>
          ) : (
            <FlexBox
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              padding="500"
            >
              <Spinner />
              <Box margin="300">
                <Text.Paragraph variant="2">
                  Generating new key...
                </Text.Paragraph>
              </Box>
            </FlexBox>
          )}
        </FlexBox>
      </ModalBody>

      <ModalFoot
        className={styles.modalFooter}
        cancelOnClick={hide}
        confirmName="Save"
        confirmOnClick={handleSubmit(onAdd)}
        confirmProps={{
          disabled: autoGeneratedSecret === undefined || isSubmitting,
        }}
      />
    </Modal>
  );
};
